import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Send, Paperclip, Check, CheckCheck, ArrowLeft, Search, FileText, MessageCircle, Loader2, Trash2, Ban } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useChat } from "../context/ChatContext";

const token = () => localStorage.getItem("ah_token") || "";
// a.url già include il prefisso "/api" → anteponiamo la base SENZA "/api"
// (usare API=BACKEND_URL+"/api" produrrebbe un doppio "/api/api" → 404 Not Found).
const ATT_BASE = process.env.REACT_APP_BACKEND_URL || "";
const attUrl = (a, dl = false) => `${ATT_BASE}${a.url}?token=${encodeURIComponent(token())}${dl ? "&download=1" : ""}`;

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
function fmtSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function initials(name, email) {
  const s = (name || email || "?").trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (s.slice(0, 2)).toUpperCase();
}
function acctChip(t) {
  if (t === "trader") return { label: "Trader", cls: "bg-amber-100 text-amber-800" };
  if (t === "real_estate") return { label: "Real Estate", cls: "bg-emerald-100 text-emerald-800" };
  return { label: "Studio", cls: "bg-slate-100 text-slate-700" };
}
function upsert(list, msg) {
  const i = list.findIndex((m) => m.id === msg.id);
  if (i >= 0) { const c = [...list]; c[i] = msg; return c; }
  return [...list, msg];
}

function Attachment({ a }) {
  if (a.is_image) {
    return (
      <a href={attUrl(a)} target="_blank" rel="noreferrer" className="block mt-1">
        <img src={attUrl(a)} alt={a.filename} className="max-h-48 max-w-[220px] rounded-md border border-ink/10 object-cover" />
      </a>
    );
  }
  return (
    <a
      href={attUrl(a, true)}
      target="_blank"
      rel="noreferrer"
      className="mt-1 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-black/5 hover:bg-black/10 transition-colors text-xs"
      data-testid="chat-attachment"
    >
      <FileText size={16} className="shrink-0" />
      <span className="truncate max-w-[180px]">{a.filename}</span>
      <span className="opacity-60">{fmtSize(a.size)}</span>
    </a>
  );
}

function Bubble({ msg, isOwn, meIsAdmin, onDelete }) {
  const read = meIsAdmin ? msg.read_by_user : msg.read_by_admin;

  if (msg.deleted) {
    return (
      <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`} data-testid="chat-message">
        <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm italic inline-flex items-center gap-1.5 ${
          isOwn ? "bg-ink/40 text-cream/80" : "bg-black/5 text-ink/50 border border-ink/10"
        }`} data-testid="chat-message-deleted">
          <Ban size={13} className="shrink-0" /> Messaggio eliminato
          <span className="ml-1 text-[10px] not-italic opacity-60">{fmtTime(msg.created_at)}</span>
        </div>
      </div>
    );
  }

  const trashBtn = onDelete ? (
    <button
      onClick={onDelete}
      title="Elimina messaggio"
      data-testid="chat-delete-btn"
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 text-ink/40 hover:text-rose-600 shrink-0"
    >
      <Trash2 size={14} />
    </button>
  ) : null;

  return (
    <div className={`group flex items-center gap-1 ${isOwn ? "justify-end" : "justify-start"}`} data-testid="chat-message">
      {isOwn && trashBtn}
      <div
        className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          isOwn ? "bg-ink text-cream rounded-br-sm" : "bg-white text-ink border border-ink/10 rounded-bl-sm"
        }`}
      >
        {!isOwn && (
          <div className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5">{msg.sender_name}</div>
        )}
        {msg.body && <div className="whitespace-pre-wrap break-words">{msg.body}</div>}
        {(msg.attachments || []).map((a) => <Attachment key={a.id} a={a} />)}
        <div className={`flex items-center gap-1 justify-end mt-1 text-[10px] ${isOwn ? "text-cream/70" : "text-ink/50"}`}>
          <span>{fmtTime(msg.created_at)}</span>
          {isOwn && (read
            ? <CheckCheck size={13} className="text-sky-300" />
            : <Check size={13} />)}
        </div>
      </div>
      {!isOwn && trashBtn}
    </div>
  );
}

function Composer({ onSend, sending }) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const pick = (e) => {
    const chosen = Array.from(e.target.files || []);
    const tooBig = chosen.find((f) => f.size > 25 * 1024 * 1024);
    if (tooBig) { toast.error(`"${tooBig.name}" supera i 25MB`); }
    setFiles((prev) => [...prev, ...chosen.filter((f) => f.size <= 25 * 1024 * 1024)].slice(0, 8));
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (sending) return;
    if (!body.trim() && files.length === 0) return;
    await onSend(body, files);
    setBody("");
    setFiles([]);
  };

  return (
    <div className="border-t border-ink/10 p-2 bg-cream">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-pending-files">
          {files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-white border border-ink/10 rounded-full pl-2 pr-1 py-0.5 text-[11px]">
              <FileText size={12} /> <span className="truncate max-w-[120px]">{f.name}</span>
              <button onClick={() => removeFile(i)} className="p-0.5 hover:text-rose-600"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="p-2 rounded-full hover:bg-ink/5 text-slate1 shrink-0"
          title="Allega file"
          data-testid="chat-attach-btn"
        >
          <Paperclip size={18} />
        </button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={pick} data-testid="chat-file-input" />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          rows={1}
          placeholder="Scrivi un messaggio..."
          className="flex-1 resize-none max-h-28 rounded-2xl border border-ink/15 px-3 py-2 text-sm focus:outline-none focus:border-ink/40 bg-white"
          data-testid="chat-input"
        />
        <button
          type="button"
          onClick={submit}
          disabled={sending || (!body.trim() && files.length === 0)}
          className="p-2.5 rounded-full bg-ink text-cream shrink-0 disabled:opacity-40 hover:bg-ink/90 transition-colors"
          data-testid="chat-send-btn"
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
}

function Thread({ messages, meIsAdmin, emptyLabel, onDeleteMessage }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-cream2/40" data-testid="chat-thread">
      {messages.length === 0 && (
        <div className="h-full flex items-center justify-center text-center text-sm text-ink/40 px-6">
          {emptyLabel}
        </div>
      )}
      {messages.map((m) => {
        const isOwn = meIsAdmin ? m.sender_role === "admin" : m.sender_role === "user";
        const canDelete = !m.deleted && (meIsAdmin || isOwn);
        return (
          <Bubble
            key={m.id}
            msg={m}
            meIsAdmin={meIsAdmin}
            isOwn={isOwn}
            onDelete={canDelete ? () => onDeleteMessage?.(m.id) : null}
          />
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

/* ------------------------- USER CHAT ------------------------- */
function UserChat({ subscribe, refreshUnread, userId, setActiveConversation }) {
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setActiveConversation(userId);
    return () => setActiveConversation(null);
  }, [setActiveConversation, userId]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/thread");
      setMessages(data.messages || []);
      await api.post("/chat/thread/read");
      refreshUnread();
    } catch { /* silent */ }
  }, [refreshUnread]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => subscribe((ev) => {
    if (ev.type === "chat.message" && ev.conversation_id === userId) {
      setMessages((prev) => upsert(prev, ev.message));
      if (ev.message.sender_role === "admin") {
        api.post("/chat/thread/read").then(refreshUnread).catch(() => {});
      }
    } else if (ev.type === "chat.read" && ev.reader_role === "admin") {
      setMessages((prev) => prev.map((m) => (m.sender_role === "user" ? { ...m, read_by_admin: true } : m)));
    } else if (ev.type === "chat.deleted" && ev.conversation_id === userId) {
      setMessages((prev) => (prev.some((m) => m.id === ev.message_id) ? upsert(prev, ev.message) : prev));
    }
  }), [subscribe, userId, refreshUnread]);

  const onDeleteMessage = async (id) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      const { data } = await api.delete(`/chat/messages/${id}`);
      setMessages((prev) => upsert(prev, data));
      refreshUnread();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const send = async (body, files) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("body", body || "");
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/chat/messages", fd);
      setMessages((prev) => upsert(prev, data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return (
    <>
      <Thread messages={messages} meIsAdmin={false} onDeleteMessage={onDeleteMessage} emptyLabel="Scrivici pure: il team ARCHI-HUB ti risponderà qui." />
      <Composer onSend={send} sending={sending} />
    </>
  );
}

/* ------------------------- ADMIN CHAT ------------------------- */
function AdminChat({ subscribe, refreshUnread, setActiveConversation }) {
  const [conversations, setConversations] = useState([]);
  const [q, setQ] = useState("");
  const [activeCid, setActiveCid] = useState(null);
  const [client, setClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const activeRef = useRef(null);
  useEffect(() => { activeRef.current = activeCid; setActiveConversation(activeCid); }, [activeCid, setActiveConversation]);
  useEffect(() => () => setActiveConversation(null), [setActiveConversation]);

  const loadConversations = useCallback(async (query) => {
    try {
      const { data } = await api.get("/chat/conversations", { params: query ? { q: query } : {} });
      setConversations(data.conversations || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => {
    const id = setTimeout(() => loadConversations(q), 250);
    return () => clearTimeout(id);
  }, [q, loadConversations]);

  const openConversation = useCallback(async (cid, clientInfo) => {
    setActiveCid(cid);
    setClient(clientInfo || null);
    try {
      const { data } = await api.get(`/chat/conversations/${cid}/thread`);
      setMessages(data.messages || []);
      setClient(data.client);
      await api.post(`/chat/conversations/${cid}/read`);
      refreshUnread();
      loadConversations(q);
    } catch { /* silent */ }
  }, [refreshUnread, loadConversations, q]);

  useEffect(() => subscribe((ev) => {
    if (ev.type === "chat.message") {
      loadConversations(activeRef.current ? undefined : q);
      if (ev.conversation_id === activeRef.current) {
        setMessages((prev) => upsert(prev, ev.message));
        if (ev.message.sender_role === "user") {
          api.post(`/chat/conversations/${activeRef.current}/read`).then(refreshUnread).catch(() => {});
        }
      }
    } else if (ev.type === "chat.read") {
      if (ev.conversation_id === activeRef.current && ev.reader_role === "user") {
        setMessages((prev) => prev.map((m) => (m.sender_role === "admin" ? { ...m, read_by_user: true } : m)));
      }
      loadConversations(q);
    } else if (ev.type === "chat.deleted") {
      if (ev.conversation_id === activeRef.current) {
        setMessages((prev) => (prev.some((m) => m.id === ev.message_id) ? upsert(prev, ev.message) : prev));
      }
      loadConversations(q);
    }
  }), [subscribe, q, loadConversations, refreshUnread]);

  const onDeleteMessage = async (id) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      const { data } = await api.delete(`/chat/messages/${id}`);
      setMessages((prev) => upsert(prev, data));
      loadConversations(q);
      refreshUnread();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const send = async (body, files) => {
    if (!activeCid) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("body", body || "");
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/chat/conversations/${activeCid}/messages`, fd);
      setMessages((prev) => upsert(prev, data));
      loadConversations(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Lista conversazioni */}
      <div className={`${activeCid ? "hidden md:flex" : "flex"} flex-col w-full md:w-72 border-r border-ink/10 bg-white`}>
        <div className="p-2 border-b border-ink/10">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca cliente..."
              className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-ink/15 focus:outline-none focus:border-ink/40"
              data-testid="chat-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="chat-conversation-list">
          {conversations.map((c) => {
            const chip = acctChip(c.client_account_type);
            return (
              <button
                key={c.client_id}
                onClick={() => openConversation(c.client_id, c)}
                className={`w-full text-left px-3 py-2.5 border-b border-ink/5 hover:bg-cream2/60 transition-colors flex items-center gap-2 ${activeCid === c.client_id ? "bg-cream2" : ""}`}
                data-testid={`chat-conv-${c.client_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-ink text-cream flex items-center justify-center text-xs font-semibold shrink-0">
                  {initials(c.client_name, c.client_email)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-sm font-medium text-ink truncate">{c.client_name || c.client_email}</span>
                    {c.last_message_at && <span className="text-[10px] text-ink/40 shrink-0">{fmtTime(c.last_message_at)}</span>}
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs text-ink/50 truncate">
                      {c.last_message_preview || <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${chip.cls}`}>{chip.label}</span>}
                    </span>
                    {c.unread > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-rose-600 text-cream text-[10px] font-semibold shrink-0" data-testid={`chat-conv-unread-${c.client_id}`}>
                        {c.unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
          {conversations.length === 0 && (
            <div className="p-6 text-center text-sm text-ink/40">Nessun cliente trovato</div>
          )}
        </div>
      </div>

      {/* Thread */}
      <div className={`${activeCid ? "flex" : "hidden md:flex"} flex-col flex-1 min-w-0`}>
        {activeCid ? (
          <>
            <div className="flex items-center gap-2 px-3 py-2 border-b border-ink/10 bg-white">
              <button onClick={() => setActiveCid(null)} className="md:hidden p-1 -ml-1 text-ink/60"><ArrowLeft size={18} /></button>
              <div className="w-8 h-8 rounded-full bg-ink text-cream flex items-center justify-center text-[11px] font-semibold shrink-0">
                {initials(client?.name, client?.email)}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink truncate">{client?.name || client?.email}</div>
                <div className="text-[11px] text-ink/50 truncate">{client?.email}</div>
              </div>
            </div>
            <Thread messages={messages} meIsAdmin onDeleteMessage={onDeleteMessage} emptyLabel="Nessun messaggio. Scrivi per iniziare la conversazione." />
            <Composer onSend={send} sending={sending} />
          </>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center text-center text-sm text-ink/40 px-8">
            Seleziona un cliente per aprire la conversazione
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------- WIDGET ROOT ------------------------- */
export default function ChatWidget() {
  const { open, setOpen, user, subscribe, refreshUnread, setActiveConversation } = useChat();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const isAdmin = user?.role === "admin";
  const panelSize = useMemo(
    () => (isAdmin ? "w-[94vw] max-w-[880px] h-[80vh] max-h-[640px]" : "w-[94vw] max-w-[400px] h-[72vh] max-h-[600px]"),
    [isAdmin]
  );

  if (!user || !open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[115] bg-black/20 md:bg-transparent" onClick={() => setOpen(false)} />
      <div
        className={`fixed z-[120] right-2 sm:right-4 bottom-2 sm:bottom-4 ${panelSize} bg-cream rounded-xl shadow-2xl border border-ink/10 flex flex-col overflow-hidden`}
        data-testid="chat-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 bg-ink text-cream">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} />
            <div>
              <div className="text-sm font-semibold tracking-wide">{isAdmin ? "Chat clienti" : "Assistenza ARCHI-HUB"}</div>
              <div className="text-[10px] text-cream/60">{isAdmin ? "Casella condivisa · rispondi ai clienti" : "Scrivici, ti rispondiamo qui"}</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="p-1 hover:bg-cream/10 rounded" data-testid="chat-close-btn"><X size={18} /></button>
        </div>
        {isAdmin
          ? <AdminChat subscribe={subscribe} refreshUnread={refreshUnread} setActiveConversation={setActiveConversation} />
          : <UserChat subscribe={subscribe} refreshUnread={refreshUnread} userId={user.id} setActiveConversation={setActiveConversation} />}
      </div>
    </>
  );
}
